import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import PageNav from '../components/PageNav'
import { to } from '../lib/nav'

import { ApiError, type StudyImages, apiFetch, getStudyImages } from '../lib/api'

// Image inventory.
//
// Behavioural specification: `oldcode/VerifyLocation.aspx.vb`, the worklist's
// **View Online** command. `BindPage()` writes the study's image count, then one
// line per series — `SeriesInstanceUID & " - " & ImageCollection.Count` — into
// the raw response with no markup around it beyond an unclosed `<TABLE>`.
//
// The same two numbers are here, in a table that closes, with the one thing the
// legacy never did with them: compared. `Series.ImageCollection` counts by
// `SeriesInstanceUID` and `Study.ImageCollection` by `StudyInstanceUID`, so the
// per-series lines and the total at the top are two different questions, and on
// some studies they give two different answers.
//
// Read-only. The page's other half, `cmdUpdateLocation_Click`, moves files
// between archive shares; it is reported at the bottom, not reproduced.

interface StudyName {
  patient_name: string | null
  study_type: string | null
  study_date: string | null
}

export default function StudyImagesPage() {
  const { studyId } = useParams()
  const [params] = useSearchParams()
  const [data, setData] = useState<StudyImages | null>(null)
  const [study, setStudy] = useState<StudyName | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Counting each series across every study scans a 118-million-row table once
  // per series, because no index leads on `SeriesInstanceUID` — 67 seconds for
  // a study with 111 of them. It answers a real question (an image filed under
  // the wrong study) so it is offered, but only when asked for.
  const [crossStudy, setCrossStudy] = useState(false)
  const [counting, setCounting] = useState(false)

  useEffect(() => {
    setData(null)
    setError(null)
    setCrossStudy(false)
    getStudyImages(Number(studyId))
      .then(setData)
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)))
  }, [studyId])

  async function loadCrossStudyCounts() {
    setCounting(true)
    setError(null)
    try {
      setData(await getStudyImages(Number(studyId), true))
      setCrossStudy(true)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setCounting(false)
    }
  }

  // Labels only; a failure here must not blank the inventory.
  useEffect(() => {
    setStudy(null)
    apiFetch<StudyName>(`/api/v1/studies/${studyId}`)
      .then(setStudy)
      .catch(() => setStudy(null))
  }, [studyId])

  const back = to('/', params)

  if (error) {
    return (
      <main className="page">
        <p className="banner banner-error">{error}</p>
        <Link to={back}>Back to worklist</Link>
      </main>
    )
  }
  if (!data) {
    return (
      <main className="page">
        <p className="muted">Counting images…</p>
      </main>
    )
  }

  const listed = data.series.filter((s) => !s.orphan)
  const orphans = data.series.filter((s) => s.orphan)

  return (
    <main className="page page-wide">
      {/* `VerifyLocation.aspx` has no header of its own; this page hangs off a
          study exactly as a worksheet does, so it carries the worksheet strip
          (DEC-023). */}
      <PageNav strip="images" studyId={studyId} />

      <div className="page-head">
        <h1>Images</h1>
        <p className="subtle">
          {study?.study_type ? `${study.study_type} study` : 'Study'} {studyId}
          {study?.patient_name ? ` · ${study.patient_name}` : ''}
          {study?.study_date ? ` · ${String(study.study_date).slice(0, 10)}` : ''}
        </p>
      </div>

      <div className="cols">
        <fieldset>
          <legend>Totals</legend>
          <div className="ro-field">
            <span className="ro-label">Images in study</span>
            <span className="ro-value">{data.study_image_count.toLocaleString()}</span>
            <span className="ro-note">
              <code>mStudy.ImageCollection.Count</code> — by StudyInstanceUID, the
              number the legacy prints first.
            </span>
          </div>
          <div className="ro-field">
            <span className="ro-label">Sum of series</span>
            <span className="ro-value">
              {data.series_image_total.toLocaleString()}
              {data.series_image_total !== data.study_image_count && (
                <span className="hint-warn"> · differs</span>
              )}
            </span>
            <span className="ro-note">
              The per-series lines added up — by SeriesInstanceUID. The legacy
              prints both numbers and never compares them.
            </span>
          </div>
          <div className="ro-field">
            <span className="ro-label">Series</span>
            <span className="ro-value">{data.series_count.toLocaleString()}</span>
          </div>
        </fieldset>

        <fieldset>
          <legend>Study</legend>
          <div className="ro-field">
            <span className="ro-label">StudyInstanceUID</span>
            <span className="ro-value mono">
              {data.study_instance_uid || <span className="muted">none</span>}
            </span>
            <span className="ro-note">
              The only key <code>Series</code> and <code>Images</code> are joined
              on — neither table carries a study id.
            </span>
          </div>
          <div className="ro-field">
            <span className="ro-label">Query time</span>
            <span className="ro-value">{data.elapsed_ms.toLocaleString()} ms</span>
            <span className="ro-note">
              Two statements over 2.0M and 118.3M rows, both index seeks.
            </span>
          </div>
        </fieldset>
      </div>

      {data.notes.map((note) => (
        <p className="banner banner-warn" key={note}>
          {note}
        </p>
      ))}

      <fieldset>
        <legend>Series</legend>
        {listed.length === 0 && orphans.length === 0 ? (
          <p className="muted">
            No series are recorded against this study's StudyInstanceUID.
          </p>
        ) : (
          <div className="grid-scroll">
            <p className="cross-study-note">
              {crossStudy ? (
                <span className="muted">
                  “Across all studies” counts each series everywhere it appears, which
                  is what the legacy&rsquo;s per-series line shows. A figure larger than
                  the study&rsquo;s own count means the series carries images filed under
                  another study.
                </span>
              ) : (
                <>
                  <button type="button" onClick={loadCrossStudyCounts} disabled={counting}>
                    {counting ? 'Counting…' : 'Count across all studies'}
                  </button>{' '}
                  <span className="muted">
                    Slow: no index leads on SeriesInstanceUID, so each series is counted
                    by scanning. Use it to find an image filed under the wrong study.
                  </span>
                </>
              )}
            </p>
            <table className="grid">
              <thead>
                <tr>
                  <th className="num">#</th>
                  <th>Modality</th>
                  <th>SeriesInstanceUID</th>
                  <th className="num">Images</th>
                  <th className="num">Across all studies</th>
                </tr>
              </thead>
              <tbody>
                {data.series.map((s) => (
                  <tr key={s.orphan ? `orphan:${s.series_instance_uid}` : s.series_id}>
                    <td className="num">
                      {s.series_number ?? <span className="muted">—</span>}
                    </td>
                    <td>
                      {s.orphan ? (
                        <span className="muted" title="No Series row">
                          not in Series
                        </span>
                      ) : (
                        (s.modality ?? <span className="muted">—</span>)
                      )}
                    </td>
                    <td className="mono">{s.series_instance_uid}</td>
                    <td className="num">{s.image_count_in_study.toLocaleString()}</td>
                    <td className="num">
                      {s.image_count === null ? (
                        <span className="muted" title="Not counted — see below">
                          —
                        </span>
                      ) : s.image_count === s.image_count_in_study ? (
                        <span className="muted">same</span>
                      ) : (
                        s.image_count.toLocaleString()
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </fieldset>

      <fieldset>
        <legend>Not reproduced</legend>
        <p className="hint muted">{data.update_location}</p>
        <p className="hint muted">{data.view_online}</p>
        <p className="hint muted">
          Neither table records whether the file behind a row is still on disk.
          These are the counts the database holds.
        </p>
      </fieldset>

    </main>
  )
}
